import { z } from 'zod';

/**
 * What the webhook did with one delivery.
 *
 * RevenueCat does not read this — it reads the status code. It exists for the
 * two readers that do: an operator replaying a delivery from the RevenueCat
 * dashboard while watching the response, and the e2e spec, which must be able to
 * tell "this replay changed nothing" from "this delivery granted an entitlement"
 * on the **serialized body**. Both look identical as a bare 200.
 *
 * - `granted` — an entitlement was created or extended.
 * - `lapsed` — a live entitlement moved to `grace`, `expired` or `revoked`.
 * - `unchanged` — the event was understood and correctly changed nothing: a
 *   cancellation that leaves access running to its period end, a product change
 *   that takes effect at the next renewal, a test ping.
 * - `duplicate` — this event id has been seen; the replay was a no-op (N14).
 * - `unsupported` — recorded, and deliberately answered 200: an `AMAZON`-store
 *   event or a product this deployment does not sell will not become handleable
 *   by being retried, and a 5xx would have RevenueCat redeliver it for days.
 */
export const REVENUECAT_WEBHOOK_OUTCOMES = [
  'granted',
  'lapsed',
  'unchanged',
  'duplicate',
  'unsupported',
] as const;

export const RevenueCatWebhookOutcomeSchema = z.enum(REVENUECAT_WEBHOOK_OUTCOMES);

export type RevenueCatWebhookOutcome = (typeof REVENUECAT_WEBHOOK_OUTCOMES)[number];

export const RevenueCatWebhookResultSchema = z
  .object({
    outcome: RevenueCatWebhookOutcomeSchema,
    /**
     * Why, when the outcome alone does not say — which store was unmapped, which
     * product id matched nothing. Operator-facing English, never translated and
     * never member-visible; it is also what is written to `billing_events.error`.
     */
    detail: z.string().nullable(),
  })
  .strict();

export type RevenueCatWebhookResult = z.infer<typeof RevenueCatWebhookResultSchema>;
